import type { ReactNode } from 'react';
import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { Tooltip } from '@noths/polaris-client-ribbons-design-system';
import { type IconName, Icon } from '@noths/polaris-design-system-core-components';

import * as styles from './ProductFeatures.styles';

export enum ProductFeature {
  FreeGiftWrappingAvailable,
  GiftWrappingAvailable,
  PhotoPersonalisation,
  Personalisable,
  MadeInBritain,
  MadeToOrder,
}

export interface ProductFeatureItem {
  icon: IconName;
  label: string;
  /** When present the row becomes a button that reveals this content in a tooltip. */
  tooltip?: ReactNode;
}

export interface ProductFeaturesProps {
  features: ProductFeatureItem[];
  onVisible?: (productFeaturesLabels: string) => void;
}

interface FeatureContentProps {
  icon: IconName;
  isInteractive: boolean;
  label: string;
}

const FeatureContent = ({ icon, isInteractive, label }: FeatureContentProps) => (
  <>
    <span css={styles.iconBadge}>
      <Icon name={icon} size="sm" />
    </span>
    <span css={[styles.text, isInteractive && styles.interactiveText]}>{label}</span>
  </>
);

export const ProductFeatures = ({ features, onVisible }: ProductFeaturesProps) => {
  const productFeaturesLabels = features.map(({ label }) => label).join(', ');
  const productFeaturesRef = useImpressionRef<HTMLUListElement>(() =>
    onVisible?.(productFeaturesLabels),
  );

  return (
    <ul css={styles.list} ref={productFeaturesRef}>
      {features.map(({ icon, label, tooltip }) => (
        <li css={[styles.listItem, Boolean(tooltip) && styles.listItemWithTooltip]} key={label}>
          {tooltip ? (
            <Tooltip alignment="left" content={tooltip}>
              {({ onClick }) => (
                <button css={styles.button} onClick={onClick} type="button">
                  <FeatureContent icon={icon} isInteractive label={label} />
                </button>
              )}
            </Tooltip>
          ) : (
            <FeatureContent icon={icon} isInteractive={false} label={label} />
          )}
        </li>
      ))}
    </ul>
  );
};
